export type ProcessingErrorLocale = 'nb' | 'en'

export type ProcessingErrorCopy = {
  explanation: string
  disclosureLabel: string
  disclosureSummary: string
  hideDisclosureLabel: string
  fallbackOriginalText: string
  emptyState: string
  loadingState: string
  errorState: string
  notificationsTitle: string
  notificationButtonLabel: string
}

const baseText: Record<ProcessingErrorLocale, ProcessingErrorCopy> = {
  nb: {
    explanation:
      'Laboratoriet har meldt at de ikke kunne behandle rekvisisjonen for denne prøven.',
    disclosureLabel: 'Vis original melding fra laboratoriet',
    disclosureSummary: 'Vis original melding fra laboratoriet',
    hideDisclosureLabel: 'Skjul original melding fra laboratoriet',
    fallbackOriginalText: 'Laboratoriet ga ingen egen forklaring.',
    emptyState: 'Ingen varslinger',
    loadingState: 'Laster varsler…',
    errorState: 'Kunne ikke laste varsler',
    notificationsTitle: 'Varsler',
    notificationButtonLabel: 'Varsler'
  },
  en: {
    explanation:
      'The laboratory reported that it could not process the requisition for this sample.',
    disclosureLabel: 'Show original message from the laboratory',
    disclosureSummary: 'Show original message from the laboratory',
    hideDisclosureLabel: 'Hide original message from the laboratory',
    fallbackOriginalText:
      'The laboratory did not provide any additional explanation.',
    emptyState: 'No notifications',
    loadingState: 'Loading notifications…',
    errorState: 'Could not load notifications',
    notificationsTitle: 'Notifications',
    notificationButtonLabel: 'Notifications'
  }
}

export const getProcessingErrorCopy = (
  locale: ProcessingErrorLocale
): ProcessingErrorCopy => {
  return baseText[locale]
}

export const normalizeProcessingErrorText = (
  rawText?: string | null,
  locale: ProcessingErrorLocale = 'nb'
): string => {
  if (rawText == null) {
    return baseText[locale].fallbackOriginalText
  }

  const sanitized = rawText
    .replaceAll(/<[^<>]+>/g, ' ')
    .replaceAll('\r\n', '\n')
    .trim()

  if (sanitized.length === 0) {
    return baseText[locale].fallbackOriginalText
  }

  const withoutPrefix = sanitized
    .replace(
      /^\s*(Processing failed|Requisition could not be processed|Rekvisisjonen kunne ikke behandles)\s*[:.\n-]?\s*/i,
      ''
    )
    .trim()

  return withoutPrefix.length > 0 &&
    withoutPrefix.toLocaleLowerCase() !== 'null'
    ? withoutPrefix
    : baseText[locale].fallbackOriginalText
}

export const isFailedProcessingEvent = (rawText?: string | null): boolean => {
  if (rawText == null) return false
  const trimmed = rawText.trim()
  return (
    trimmed.toLowerCase().startsWith('processing failed:') ||
    trimmed.toLowerCase().startsWith('rekvisisjonen kunne ikke behandles:') ||
    trimmed.toLowerCase().startsWith('requisition could not be processed:')
  )
}

export const shouldHideProcessingErrorDetails = (item: {
  source?: string
  entityType?: string
  kind?: string
  description?: string | null
  additionalInfo?: string | null
}): boolean => {
  const hasFailedProcessingMarker =
    isFailedProcessingEvent(item.description) ||
    isFailedProcessingEvent(item.additionalInfo)

  return (
    item.source === 'event' &&
    item.entityType === 'requisition' &&
    hasFailedProcessingMarker
  )
}
